import { config } from './configuration';

const API_URL = config.apiUrl;

type Params = Record<string, string | number | boolean | undefined>;

export type ApiResponse<T> = {
    data: T | null;
    error: string | null;
    status: number;
};

function buildUrl(endpoint: string, params?: Params): string {
    let url = `${API_URL}${endpoint}`;

    if (params) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
                searchParams.append(key, String(value));
            }
        });
        const query = searchParams.toString();
        if (query) url += `?${query}`;
    }

    return url;
}

function normalizeErrorMessage(message: unknown, fallback: string): string {
    if (Array.isArray(message)) {
        return message.join(', ');
    }

    if (typeof message === 'string') {
        return message;
    }

    return fallback;
}

async function apiFetch<T>(
    endpoint: string,
    options: {
        method: string;
        token?: string;
        body?: unknown;
        params?: Params;
        signal?: AbortSignal;
    }
): Promise<ApiResponse<T>> {
    const { method, token, body, params, signal } = options;
    try {
        const response = await fetch(buildUrl(endpoint, params), {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            ...(body !== undefined && { body: JSON.stringify(body) }),
            signal,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                data: null,
                error: normalizeErrorMessage(
                    errorData.message,
                    `Request failed with status ${response.status}`
                ),
                status: response.status,
            };
        }

        const data = await response.json().catch(() => null);
        return { data, error: null, status: response.status };
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            return { data: null, error: 'Request aborted', status: 0 };
        }
        return {
            data: null,
            error: error instanceof Error ? error.message : 'Network error',
            status: 0,
        };
    }
}

export async function apiGet<T>(
    endpoint: string,
    token?: string,
    params?: Params,
    signal?: AbortSignal
): Promise<ApiResponse<T>> {
    return apiFetch<T>(endpoint, { method: 'GET', token, params, signal });
}

export async function apiPost<T>(
    endpoint: string,
    body: unknown,
    token?: string,
    params?: Params,
    signal?: AbortSignal
): Promise<ApiResponse<T>> {
    return apiFetch<T>(endpoint, { method: 'POST', token, body, params, signal });
}

export async function apiPatch<T>(
    endpoint: string,
    body: unknown,
    token?: string,
    params?: Params,
    signal?: AbortSignal
): Promise<ApiResponse<T>> {
    return apiFetch<T>(endpoint, { method: 'PATCH', token, body, params, signal });
}

export async function apiDelete<T>(
    endpoint: string,
    token?: string,
    params?: Params,
    signal?: AbortSignal
): Promise<ApiResponse<T>> {
    return apiFetch<T>(endpoint, { method: 'DELETE', token, params, signal });
}

/**
 * Fetch a binary attachment (e.g. an XLSX export) as a Blob, with auth. Kept
 * separate from apiFetch because that one always parses JSON. Resolving the blob
 * proves the file was built and transferred — that is what gates the archive
 * button (a browser cannot reliably report the OS-level "save" completing).
 */
export async function apiDownload(
    endpoint: string,
    token?: string,
    params?: Params,
    body?: unknown
): Promise<{ blob: Blob | null; filename: string | null; error: string | null; status: number }> {
    try {
        const response = await fetch(buildUrl(endpoint, params), {
            method: body === undefined ? 'GET' : 'POST',
            headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
                ...(body !== undefined && { 'Content-Type': 'application/json' }),
            },
            ...(body !== undefined && { body: JSON.stringify(body) }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                blob: null,
                filename: null,
                error: normalizeErrorMessage(errorData.message, `Request failed with status ${response.status}`),
                status: response.status,
            };
        }

        const disposition = response.headers.get('Content-Disposition') ?? '';
        const match = disposition.match(/filename="?([^"]+)"?/);
        const blob = await response.blob();
        return { blob, filename: match?.[1] ?? null, error: null, status: response.status };
    } catch (error) {
        return {
            blob: null,
            filename: null,
            error: error instanceof Error ? error.message : 'Network error',
            status: 0,
        };
    }
}
